import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Select,
  FormControl,
  FormLabel,
  Flex,
} from '@chakra-ui/react';
import axios from '../utils/api';
import { buildLockedHierarchySelections, isSuperAdminRole } from '../utils/auth';

const base_url = process.env.REACT_APP_API_URL_ADMIN;

function filledEntries(values = {}) {
  return Object.fromEntries(
    Object.entries(values || {}).filter(([, v]) => v != null && v !== '')
  );
}

const CascadingDropdownFilter = ({
  onSelectionChange,
  initialValues,
  roleBeingCreated,
  userHierarchy,
}) => {
  const [selections, setSelections] = useState(() => filledEntries(initialValues));
  const [options, setOptions] = useState({});
  const { user } = useSelector((state) => state.auth);
  const [visibleLevels, setVisibleLevels] = useState([]);
  const lockedNotifiedRef = useRef(false);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  useEffect(() => {
    if (!Array.isArray(userHierarchy) || !userHierarchy.length) {
      setVisibleLevels([]);
      return;
    }
    const userRoleIndex = userHierarchy.findIndex((level) => level.role === user?.role);
    const createdRoleIndex = userHierarchy.findIndex((level) => level.role === roleBeingCreated);
    const startIndex = isSuperAdminRole(user?.role)
      ? 0
      : userRoleIndex >= 0
        ? userRoleIndex + 1
        : 0;
    const endIndex = createdRoleIndex >= 0 ? createdRoleIndex : userHierarchy.length;
    setVisibleLevels(userHierarchy.slice(startIndex, Math.max(startIndex, endIndex)));

    const locked = buildLockedHierarchySelections(user);
    if (!lockedNotifiedRef.current && Object.keys(locked).length > 0) {
      lockedNotifiedRef.current = true;
      setSelections((prev) => {
        const next = { ...locked, ...filledEntries(prev), ...filledEntries(initialValues) };
        onSelectionChangeRef.current?.(next);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, roleBeingCreated, userHierarchy]);

  const initialFilledKey = JSON.stringify(filledEntries(initialValues));
  useEffect(() => {
    const filled = filledEntries(JSON.parse(initialFilledKey || '{}'));
    if (Object.keys(filled).length === 0) return;
    setSelections((prev) => {
      const changed = Object.keys(filled).some((k) => String(prev[k] || '') !== String(filled[k]));
      return changed ? { ...prev, ...filled } : prev;
    });
  }, [initialFilledKey]);

  useEffect(() => {
    let cancelled = false;

    const fetchOptions = async () => {
      if (!visibleLevels.length) {
        if (!cancelled) setOptions({});
        return;
      }

      const updatedOptions = {};
      for (let i = 0; i < visibleLevels.length; i++) {
        const level = visibleLevels[i];
        const parentReady = i === 0 || selections[visibleLevels[i - 1].key];
        if (!parentReady) {
          updatedOptions[level.key] = [];
          continue;
        }

        const filters = {};
        if (i === 0) {
          const hierarchy = Array.isArray(userHierarchy) ? userHierarchy : [];
          const levelIdx = hierarchy.findIndex((l) => l.key === level.key);
          for (let j = 0; j < levelIdx; j++) {
            const parentKey = hierarchy[j].key;
            if (selections[parentKey]) {
              filters[parentKey] = selections[parentKey];
            }
          }
        } else {
          filters[visibleLevels[i - 1].key] = selections[visibleLevels[i - 1].key];
        }

        try {
          const response = await axios.post(`${base_url}`, {
            apiName: 'user',
            command: 'getOptions',
            level: level.key,
            filters,
          });
          updatedOptions[level.key] = Array.isArray(response.data?.options)
            ? response.data.options
            : [];
        } catch (error) {
          console.error(`Error fetching options for ${level.key}:`, error);
          updatedOptions[level.key] = [];
        }
      }

      if (!cancelled) setOptions(updatedOptions);
    };

    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [selections, visibleLevels, userHierarchy]);

  const handleChange = (level, value) => {
    const updatedSelections = { ...selections, [level]: value };
    const levelIndex = visibleLevels.findIndex((l) => l.key === level);
    visibleLevels.slice(levelIndex + 1).forEach((l) => {
      updatedSelections[l.key] = '';
    });
    setSelections(updatedSelections);
    // Omit empty cleared child keys so APIs never receive agent_id: '' etc.
    onSelectionChange?.(filledEntries(updatedSelections));
  };

  return (
    <Flex direction="row" wrap="wrap" gap={4}>
      {visibleLevels.map((level) => {
        const levelIndex = visibleLevels.findIndex((l) => l.key === level.key);
        const parentKey = levelIndex > 0 ? visibleLevels[levelIndex - 1].key : null;
        return (
          <FormControl key={level.key} flex="1">
            <FormLabel htmlFor={level.key}>{level.label}</FormLabel>
            <Select
              id={level.key}
              placeholder={`Select ${level.label}`}
              value={selections[level.key] || ''}
              onChange={(e) => handleChange(level.key, e.target.value)}
              isDisabled={levelIndex > 0 && !selections[parentKey]}
            >
              {(options[level.key] || []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
        );
      })}
    </Flex>
  );
};

export default CascadingDropdownFilter;
